import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHeartbeatReplies } from '../../../services/queries';
import { readFilters } from '../../composite/FilterBar';
import { STANCE_META, TARGET_META, STANCES, TARGETS, SIGNALS, SIGNAL_META } from '../../../constants/sentiment';

/**
 * Stance leads the filters: "against you" is the question the drawer exists
 * to answer. Target is second - "at you" versus "at someone else" is what
 * separates an ally's anger from an attacker's. Emotion comes third; its
 * options are filled from the taxonomy by the page.
 */
export const REPLY_FILTERS = [
	{ key: 'stance', label: 'Standing', options: [...STANCES.map((s) => ({ value: s, label: STANCE_META[s].label })), { value: 'unclear', label: 'Unclear' }] },
	{ key: 'target', label: 'Aimed at', options: TARGETS.filter((t) => t !== 'none').map((t) => ({ value: t, label: TARGET_META[t].label })) },
	{ key: 'emotion', label: 'Emotion', multi: true, options: [] },
	// "Is anyone threatening to sue?" - a filter, not something to find by
	// reading. "Anything flagged" is the option managers actually use.
	{ key: 'signal', label: 'Flagged', options: [{ value: 'any', label: 'Anything flagged' }, ...SIGNALS.map((g) => ({ value: g, label: SIGNAL_META[g].label }))] },
];

/**
 * The evidence drawer's state and its replies query, scoped to one run.
 *
 * Extracted because the account page and the run page both open the same drawer
 * against the same endpoint; the only difference is which run they scope to.
 * Duplicating the params memo across both was how the two would drift.
 */
export function useVoices(accountId, runId, revision) {
	const [searchParams] = useSearchParams();
	const [voices, setVoices] = useState(null); // { parentPostId?, emotion?, stance?, signal?, url?, title, text }

	const filters = readFilters(searchParams, REPLY_FILTERS);
	const sort = searchParams.get('sort') || 'recent';

	const replyParams = useMemo(() => ({
		parentPostId: voices?.parentPostId || undefined,
		// A cell or a strip can preset stance/emotion; the chips refine from there.
		// comma-joined: the backend splits on commas (Express 5 does not parse repeated keys into arrays)
		emotion: voices?.emotion ? voices.emotion : (filters.emotion?.length ? filters.emotion.join(',') : undefined),
		stance: voices?.stance || filters.stance || undefined,
		target: filters.target || undefined,
		// A lane can open the drawer already narrowed to one signal.
		signal: voices?.signal || filters.signal || undefined,
		runId,
		...(revision ? { revision } : {}),
		sort,
	}), [voices, filters.emotion, filters.stance, filters.target, filters.signal, runId, sort, revision]);

	const repliesQ = useHeartbeatReplies(accountId, replyParams, { enabled: !!voices });

	return {
		voices,
		openVoices: setVoices,
		closeVoices: () => setVoices(null),
		replies: (repliesQ.data?.pages || []).flatMap((p) => p.replies),
		repliesLoading: repliesQ.isFetching,
		hasMoreReplies: !!repliesQ.hasNextPage,
		loadMoreReplies: () => repliesQ.fetchNextPage(),
	};
}
